import FormElement from './FormElement';
import StyleMixin from './mixins/styleMixins/StyleMixin';
import DisabledMixin from './mixins/DisabledMixin';
import TextInputMixin from './mixins/TextInputMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import FocusMixin from './mixins/FocusMixin';
import ReadOnlyMixin from './mixins/ReadOnlyMixin';
import ClickableMixin from './mixins/ClickableMixin';
import mixin from '../privates/utils/mixin';
import * as members from '../privates/privateMembers';
import {validate} from '../privates/validations/validation';
import RequiredMixin from './mixins/RequiredMixin';

/**
 * @class TextBox
 * @summary An input element for capturing large amounts of text.
 * @mixes $w.FormElement
 * @mixes $w.DisabledMixin
 * @mixes $w.TextInputMixin
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.FocusMixin
 * @mixes $w.ReadOnlyMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.StyleMixin
 * @mixes $w.RequiredMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.inputs.TextAreaInput
 * @definitionId wysiwyg.viewer.components.inputs.TextAreaInput
 * @tagname textarea
 */
class TextBox extends FormElement {

    /**
     * @member value
     * @label property
     * @syntax
     * get value(): string
     * set value(value: string): void
     * @summary Sets or gets a text input's value.
     * @description
     *  To reset the text box, restoring any placeholder text, set the `value`
     *  property to `null` or `undefined`.
     *
     * > **Notes:**
     * > + Changing a text box's `value` in code does not trigger an `onChange` event.
     * >
     * >
     * > + If an element is connected to a dataset, setting the element's `value`
     *  in code does not set the value of the connected field in the dataset.
     *  That means if you use the dataset to perform a submit, the value changed
     *  in code is not reflected in the submitted item.
     * >
     * >
     * > + To submit the new value using a dataset, set the field's value using the
     *  [`setFieldValue()`](wix-dataset.Dataset.html#setFieldValue) function before
     *  performing the submit.
     * @snippet [FormElement-value_get.es6=Get an element's value]
     * @snippet [FormElement-value_set.es6=Set an element's value]
     * @type {external:String}
     * @memberof $w.TextBox
     * @override
     */

  /**
   * @member value
   * @memberof $w.TextBox
   * @param {external:String} value
   */


  /**
     * @member wrap
     * @label property
     * @syntax
     * get wrap(): string
     * set wrap(value: WrapType): void
     * type WrapType = "hard" | "soft"
     * @summary Determines if the text contains line breaks when its value is read.
     * @description
     *  Setting the `wrap` property sets how the text box's contents
     *  are returned when its value is read. Setting `wrap` to `"hard"`
     *  means line breaks are automatically inserted into the text. The width of
     *  the text box determines where the line breaks are placed. Setting `wrap`
     *  to `"soft"` means line breaks are not inserted when the value of the
     *  text box is read.
     *
     *  Getting the `wrap` property gets how the text box's contents
     *  are returned when its value is read.
     * @snippet [TextBox-wrap_get.es6=Get a text box's wrapping]
     * @snippet [TextBox-wrap_set.es6=Set a text box's wrapping]
     * @default "soft"
     * @type {external:String}
     * @memberof $w.TextBox
     * @instance
     */
    get wrap() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getProps(compId).wrap;
    }

  /**
   * @member wrap
   * @memberof $w.TextBox
   * @param {external:String} wrap
   */
    set wrap(wrap) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'wrap',
            dataToValidate: {wrap},
            compName: this.id
        })) {
            return;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        RMI.setProps(compId, { wrap });
    }

    toJSON() {
        const {id, type, global, rendered, readOnly, required, value, wrap} = this;
        return {id, type, global, rendered, readOnly, required, value, wrap};
    }

    /**
     * @member style
     * @label property
     * @syntax
     * get style(): Style
     * @summary Gets an object containing information about the text box's styles.
     * @description
     *  The following styles can be used with text boxes:
     *
     *  + [`backgroundColor`]($w.Style.html#backgroundColor)
     *  + [`borderColor`]($w.Style.html#borderColor)
     *  + [`borderRadius`]($w.Style.html#borderRadius)
     *  + [`borderWidth`]($w.Style.html#borderWidth)
     *  + [`color`]($w.Style.html#color)
     *
     *
     *  Getting or setting a text box's styles, gets or sets the styles of the
     *  text box's regular state. It does not set the styles of the text box's hover,
     *  focus, error, or disabled states.
     * @snippet [BackgroundColorMixin-backgroundColor_set.es6=Set the background color]
     * @snippet [BackgroundColorMixin-backgroundColor_get.es6=Get the background color]
     * @type {$w.Style}
     * @memberof $w.TextBox
     * @readonly
     * @override
     */
}

mixin(TextBox.prototype, DisabledMixin.prototype);
mixin(TextBox.prototype, HiddenCollapsedMixin.prototype);
mixin(TextBox.prototype, TextInputMixin.prototype);
mixin(TextBox.prototype, FocusMixin.prototype);
mixin(TextBox.prototype, ReadOnlyMixin.prototype);
mixin(TextBox.prototype, ClickableMixin.prototype);
mixin(TextBox.prototype, StyleMixin.prototype);
mixin(TextBox.prototype, RequiredMixin.prototype);

export default TextBox;
